import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@shared/api/client';
import { TASKS } from '@shared/api/endpoints';
import type { CreateTaskSessionRequest, TaskSession } from '@tailorcast/shared-types';

export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateTaskSessionRequest) => api.post<TaskSession>(TASKS.CREATE, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useApprovePlan(taskId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<{ stage: string }>(TASKS.APPROVE(taskId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
    },
  });
}

export function useRejectPlan(taskId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (feedback: string) => api.post<TaskSession>(TASKS.REJECT(taskId), { feedback }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
    },
  });
}

export function usePauseTask(taskId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<{ stage: string }>(TASKS.PAUSE(taskId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
    },
  });
}

export function useResumeTask(taskId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<{ stage: string }>(TASKS.RESUME(taskId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
    },
  });
}

export function useCancelTask(taskId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<{ stage: string }>(TASKS.CANCEL(taskId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useClarifyTask(taskId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (answers: Record<string, string>) =>
      api.post<TaskSession>(TASKS.CLARIFY(taskId), { answers }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
    },
  });
}
